import { useEffect } from 'react';
import { Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { Link, type Href, usePathname, useRouter } from 'expo-router';
import { WebHead } from '../../../components';
import { GUIDE_ARTICLES } from '../../../lib/content/articles';
import {
  trackMarketingCtaClicked,
  trackMarketingPageViewed,
} from '../../../lib/product-events';

export default function GuidesIndexPage() {
  const pathname = usePathname();
  const router = useRouter();
  const canonicalUrl = `${typeof globalThis.location?.origin === 'string'
    ? globalThis.location.origin
    : process.env['EXPO_PUBLIC_SITE_URL'] ?? 'https://perdiem.app'}/guides/`;

  useEffect(() => {
    trackMarketingPageViewed({
      route: pathname,
      pageType: 'guide_hub',
      pageSlug: 'guides',
      title: 'Per Diem product guides',
    });
  }, [pathname]);

  if (Platform.OS !== 'web') return null;

  return (
    <>
      <WebHead>
        <title>Per Diem Product Guides</title>
        <meta
          name="description"
          content="Read product guides about Per Diem onboarding, daily spending guidance, and connected household FIRE planning."
        />
        <link rel="canonical" href={canonicalUrl} />
      </WebHead>

      <ScrollView className="flex-1 bg-stone-50" contentContainerStyle={{ paddingBottom: 64 }}>
        <View className="mx-auto w-full max-w-5xl px-6 pt-12">
          <Text className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-700">
            Guides
          </Text>
          <Text className="mt-4 max-w-3xl text-5xl font-semibold leading-[56px] text-stone-950">
            Walkthroughs for how Per Diem actually works.
          </Text>
          <Text className="mt-5 max-w-3xl text-lg leading-8 text-stone-600">
            These are product guides for people who want to understand the onboarding
            flow, the daily-spending model, and the connected household planning layer
            before they sign up.
          </Text>

          <View className="mt-8 flex-row flex-wrap gap-3">
            <Pressable
              onPress={() => {
                trackMarketingCtaClicked({
                  route: pathname,
                  pageType: 'guide_hub',
                  pageSlug: 'guides',
                  ctaId: 'home',
                  destination: 'landing',
                  placement: 'hero',
                });
                router.push('/');
              }}
              className="rounded-2xl border border-stone-300 px-5 py-4"
            >
              <Text className="text-base font-semibold text-stone-800">Back to home</Text>
            </Pressable>
          </View>

          <View className="mt-10 gap-5">
            {GUIDE_ARTICLES.map((article) => (
              <Link key={article.slug} href={article.path as Href} asChild>
                <Pressable className="rounded-[28px] border border-stone-200 bg-white p-6 shadow-sm">
                  <Text className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-700">
                    {article.categoryLabel}
                  </Text>
                  <Text className="mt-3 text-2xl font-semibold text-stone-950">
                    {article.title}
                  </Text>
                  <Text className="mt-3 text-base leading-7 text-stone-600">
                    {article.excerpt}
                  </Text>
                  <Text className="mt-5 text-sm font-medium text-sky-700">
                    Read article →
                  </Text>
                </Pressable>
              </Link>
            ))}
          </View>
        </View>
      </ScrollView>
    </>
  );
}
